import React, { ReactNode, useEffect, useRef, useState } from 'react';

import { Divider, LinearProgress, Paper } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { useSnackbar } from 'notistack';

import { SelectionChangedEvent } from 'ag-grid-community';

import {
    enterPreviewMode,
    resetMode,
    setDates,
} from '../../../../../state/ui/customerService/bottomGrid';
import {
    fetchTasks,
    selectTask,
    TASKS_GRID_ID,
} from '../../../../../state/ui/customerService/bottomGrid/tasks';
import {
    useSelectedGridItemId,
    useSelectedClientsAndLocations,
    useMainMapVisible,
    useClientAndLocationPaneVisible,
    useMap,
} from '../../../../../state/ui/customerService/clientsAndLocations/index.hooks';
import {
    useTaskId,
    useTasks,
    useTasksLoading,
    useTasksOnlyNotAssigned,
    useTasksOnlyNotPlanned,
    useTasksSourceSetModel,
} from '../../../../../state/ui/customerService/bottomGrid/tasks/index.hooks';
import {
    selectClient,
    selectLocation,
} from '../../../../../state/ui/customerService/clientsAndLocations';
import {
    useBottomGrid,
    useGridDates,
} from '../../../../../state/ui/customerService/bottomGrid/index.hooks';

import { useToolkitDispatch } from '../../../../../hooks';
import TranslationHelper from '../../../../../helpers/TranslationHelper';
import { errorMessageHandler } from '../../../../../helpers/errorMessageHandler';

import { useStyles } from '../../../Themable.hooks';

import IconButtonWithTooltip from '../../../../../components/IconButtonWithTooltip';
import PaneHeader from '../../../../../components/PaneHeader';
import SearchField from '../../../../../components/common/SearchField/SearchField';
import SourceSetGrid from '../../../../../components/SourceSetGrid';
import { ISourceSetEntity } from '../../../../../state/types';
import DateRangeDisplay from '../../../../../components/DateRangeDisplay';
import TasksCheckboxFilters from './TasksCheckboxFilters';
import CscExportButtons from '../../CscExportButtons/CscExportButtons';
import {
    IFeatureSelectedOnGridParams,
    TVectorGeometrySource,
} from '../../../types';
import { zoomAfterSelect } from '../../../helpers/zoomAfterSelect';

interface IOwnProps {
    handleClose: () => void;
    titleComponent?: ReactNode;
    onFeatureSelected: (params: IFeatureSelectedOnGridParams) => void;
    source: TVectorGeometrySource;
    selectedItems: string[];
    lastSelectedBottomItem: string;
    handleSelectLastItem: (sourceSetId: string, lastItemId: string) => void;
}

const CHECKBOX_POSITION = 0;

const TasksGrid = ({
    handleClose,
    titleComponent,
    onFeatureSelected,
    source,
    selectedItems,
    lastSelectedBottomItem,
    handleSelectLastItem,
}: IOwnProps) => {
    const notInitialRender = useRef(false);

    const sourceSetModel = useTasksSourceSetModel();

    const [filter, setFilter] = useState(sourceSetModel?.quickFilter || '');
    const [gridApi, setGridApi] = useState(undefined);

    const dates = useGridDates();
    const onlyNotAssignedFromStore = useTasksOnlyNotAssigned();
    const onlyNotPlannedFromStore = useTasksOnlyNotPlanned();
    const classes = useStyles();
    const tasks = useTasks();
    const loading = useTasksLoading();
    const taskId = useTaskId();
    const selectedGridItemId = useSelectedGridItemId();
    const selectedClientsAndLocations = useSelectedClientsAndLocations();
    const mapVisible = useMainMapVisible();
    const clientAndLocationPaneVisible = useClientAndLocationPaneVisible();
    const selectedGrid = useBottomGrid();
    const map = useMap();
    const paneVisible = clientAndLocationPaneVisible || !!taskId;
    const { enqueueSnackbar } = useSnackbar();
    const toolkitDispatch = useToolkitDispatch();
    useEffect(() => {
        resetMultiSelection();
    }, [selectedGrid]);

    useEffect(() => {
        if (!loading) {
            handleFetchTasks(dates.from, dates.to)
                ?.unwrap()

                .catch((error) => {
                    const message = errorMessageHandler(error.status)();
                    enqueueSnackbar(message, {
                        variant: 'error',
                    });
                });
        }
    }, [selectedClientsAndLocations]);

    useEffect(() => {
        if (notInitialRender.current) {
            toolkitDispatch(resetMode());
        } else {
            notInitialRender.current = true;
        }
    }, [selectedGridItemId]);

    const handleFetchTasks = (from: string, to: string) => {
        if (!selectedClientsAndLocations.length) {
            return;
        }
        toolkitDispatch(setDates({ from, to }));
        const fetchTasksParams = () => {
            return onlyNotPlannedFromStore
                ? {
                      selectedClientsAndLocations,
                      onlyNotAssigned: onlyNotAssignedFromStore,
                      includeNotPlanned: true,
                  }
                : {
                      selectedClientsAndLocations,
                      from,
                      to,
                      onlyNotAssigned: onlyNotAssignedFromStore,
                      includeNotPlanned: true,
                  };
        };
        return toolkitDispatch(fetchTasks(fetchTasksParams()));
    };

    const renderRightCustomControls = () => {
        return (
            <>
                <CscExportButtons api={gridApi} prefix="tasks" />
                <IconButtonWithTooltip
                    title={TranslationHelper.translate('Close')}
                    onClick={() => handleClose()}
                >
                    <CloseIcon />
                </IconButtonWithTooltip>
            </>
        );
    };

    const onSelectionChanged = (e: SelectionChangedEvent) => {
        const selectedNodes = e.api
            .getSelectedNodes()
            .map((selected) => {
                return {
                    id: selected.data.externalId,
                    name: selected.data.locationName,
                    clientId: selected.data.clientId,
                    clientName: selected.data.name,
                    sourceSetId: selected.data.id,
                    locationId: selected.data.locationId,
                    coordinates: selected.data.coordinate,
                    radius: selected.data.radius,
                    statusId: selected.data.statusId,
                };
            })
            .filter(Boolean);
        if (!selectedNodes.length) {
            resetMultiSelection();

            return;
        }
        onFeatureSelected({
            source,
            sourceSetId: TASKS_GRID_ID,
            items: selectedNodes.map((node) => node.sourceSetId),
        });
    };

    const resetMultiSelection = () => {
        onFeatureSelected({
            source,
            sourceSetId: TASKS_GRID_ID,
            items: [],
        });
        toolkitDispatch(resetMode());
        toolkitDispatch(selectTask(null));
    };

    const handleCellInteraction = (data: ISourceSetEntity) => {
        zoomAfterSelect(source, map, data.id);

        handleSelectLastItem(TASKS_GRID_ID, data.id);

        const locationId = selectedClientsAndLocations.filter(
            (item) => item.id === data.locationId?.toString()
        )[0]?.locationId;
        toolkitDispatch(selectLocation(locationId));
        toolkitDispatch(selectClient(data.clientId));
        toolkitDispatch(enterPreviewMode());
        toolkitDispatch(selectTask(data.id));
    };
    const handleSetApiGrid = (
        gridApi: any,
        gridColumnApi: any | undefined = undefined
    ) => {
        setGridApi(gridApi);
    };

    const multiSelectProps = {
        rowSelection: 'multiple',
        checkBox: { index: CHECKBOX_POSITION },
        onSelectionChanged,
        items: selectedItems.map((item) => {
            return { sourceSetId: item };
        }),
    };
    return (
        <Paper className={classes.gridPane}>
            <div className={classes.wrapper}>
                <PaneHeader
                    title={TranslationHelper.translate('Tasks')}
                    titleComponent={titleComponent}
                    renderLeftCustomControls={() => (
                        <>
                            <SearchField
                                value={filter}
                                filterChange={setFilter}
                            />
                            <DateRangeDisplay
                                onDateChange={handleFetchTasks}
                                sourceSetId={tasks?.id}
                                dates={dates}
                                datesFormat={'short'}
                                small
                                calendarIconOnly={mapVisible && paneVisible}
                            />
                            {(!mapVisible || !paneVisible) && (
                                <TasksCheckboxFilters dates={dates} />
                            )}
                        </>
                    )}
                    renderRightCustomControls={() =>
                        renderRightCustomControls()
                    }
                />
                {!tasks || loading ? <LinearProgress /> : <Divider />}
                {tasks && (
                    <SourceSetGrid
                        sourceSet={tasks}
                        cellInteractionHandler={handleCellInteraction}
                        quickFilterValue={filter}
                        setGridApi={handleSetApiGrid}
                        sourceSetModel={sourceSetModel}
                        selectedSourceSetElementId={
                            lastSelectedBottomItem || null
                        }
                        multiSelectProps={multiSelectProps}
                    />
                )}
            </div>
        </Paper>
    );
};

export default TasksGrid;
